import { FilterAppliedResult } from './plan.type'
import { AssetDataGeoJsonLayer } from './asset.type'
import { Filter } from './filter.type'
import type { Restriction } from '@workspaces/types'

/**
 * Legacy plans may carry no `restrictions` at all, or plain int codes instead
 * of include/exclude items. `deserializeFilters` already normalizes both when
 * loading from persistence; this guard makes the helpers safe even for
 * filters that reach them through some other path.
 */
function normalizePanelRestrictions(
  restrictions:
    | Array<Restriction.PanelRestrictionFilterItem | number>
    | null
    | undefined,
): Restriction.PanelRestrictionFilterItem[] {
  return (restrictions ?? []).map((item) =>
    typeof item === 'number' ? { id: item, exclude: false } : item,
  )
}

/**
 * Panel restrictions constrain the WHOLE plan, so the widget keeps the same
 * selection on every sub-filter. This merges them back into the single
 * plan-wide selection, covering legacy plans that persisted it on a single
 * sub-filter.
 */
export function getPlanPanelRestrictions(
  filters: Filter[],
): Restriction.PanelRestrictionFilterItem[] {
  const merged = new Map<number, Restriction.PanelRestrictionFilterItem>()
  for (const filter of filters ?? []) {
    for (const item of normalizePanelRestrictions(filter?.restrictions)) {
      if (!merged.has(item.id)) {
        merged.set(item.id, item)
      }
    }
  }
  return [...merged.values()]
}

export function isPanelRestrictionInFilter(
  restrictions: Restriction.PanelRestrictionFilterItem[] | null | undefined,
): boolean {
  return (restrictions ?? []).length > 0
}

export function filterAssetsByPanelRestriction(
  assets: AssetDataGeoJsonLayer[],
  restrictions:
    | Array<Restriction.PanelRestrictionFilterItem | number>
    | null
    | undefined,
): FilterAppliedResult {
  const normalizedRestrictions = normalizePanelRestrictions(restrictions)
  if (!isPanelRestrictionInFilter(normalizedRestrictions)) {
    return { assets, filterApplied: false }
  }

  const includedCodes = normalizedRestrictions
    .filter((item) => !item.exclude)
    .map((item) => item.id)
  const excludedCodes = normalizedRestrictions
    .filter((item) => item.exclude)
    .map((item) => item.id)

  // Every selection must be complied with: included codes must all be carried
  // by the asset, excluded codes must all be absent
  const assetsFiltered = assets.filter((asset) => {
    const assetCodes = asset.properties.restrictions ?? []
    return (
      includedCodes.every((code) => assetCodes.includes(code)) &&
      excludedCodes.every((code) => !assetCodes.includes(code))
    )
  })

  return { assets: assetsFiltered, filterApplied: true }
}
